import React, { Suspense } from 'react';
import { Helmet } from 'react-helmet';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAxios } from 'hooks';
import { endPoints } from 'api/endpoints';
import { useRecoilState } from 'recoil';
import { useParams, useHistory } from 'react-router-dom';
import { catalogs } from 'store';
import ProvisioningStyles from 'pages/provisioning/vm/style';
import {
    isMandatoryTagsFullfilled,
    orderId,
    quickConfigDetails,
    sspSubmitOrder,
    toggleSspModels,
    defaultVmDataDisk,
    isConfigDiskFulfilled,
} from 'pages/provisioning/aws/oracle-19c/store';
import { AwsSQLIaasCatalogDefinitionId } from 'utils/catalogDefinitionId';
import { checkAndRedirectCatalog } from 'utils';
import { resetOrder, resetToggleSspModal, formOrderObjFromCart, formOrderObjFromOrderDetails } from './utils/index';

const ProvisioningHeader = React.lazy(() => import('pages/provisioning/aws/oracle-19c/ProvisioningHeader'));
const ApplicationDetails = React.lazy(() => import('pages/provisioning/aws/oracle-19c/ApplicationDetails'));
const SspForm = React.lazy(() => import('pages/provisioning/aws/oracle-19c/SspForm'));
const AddedToCartConfirmModal = React.lazy(() => import('pages/provisioning/aws/oracle-19c/AddedToCartConfirmModal'));
const UpdatedToCartConfirmModal = React.lazy(() => import('pages/provisioning/aws/oracle-19c/UpdatedToCartConfirmModal'));
const ShowCostDetails = React.lazy(() => import('pages/provisioning/aws/oracle-19c/ShowCostDetails'));
const ConfirmDD = React.lazy(() => import('pages/provisioning/aws/oracle-19c/ConfirmDD'));
const CurrentServerNameDetails = React.lazy(() => import('pages/provisioning/aws/oracle-19c/components/Form/ResourceSection/CurrentServerDetails'));

const AWSOracle19CProvisioningPage = () => {
    const [isLoading, setLoading] = React.useState(true);
    const [cmpCatalogs] = useRecoilState(catalogs);
    const [order, setOrder] = useRecoilState(sspSubmitOrder);
    const [vmDataDisk, setVmDataDisk] = useRecoilState(defaultVmDataDisk);
    const [, resetOrderId] = useRecoilState(orderId);
    const [, resetIsMandatoryTagsFullfilled] = useRecoilState(isMandatoryTagsFullfilled);
    const [, resetIsConfigDiskFulfilled] = useRecoilState(isConfigDiskFulfilled);
    const [, resetToggleSspModels] = useRecoilState(toggleSspModels);
    const [, resetQuickConfig] = useRecoilState(quickConfigDetails);
    const { catalogCode, cartItemId, operation, catalogServiceId } = useParams();
    const history = useHistory();
    const axios = useAxios();

    async function getCosts() {
        const { data } = await axios.get(`${endPoints.catalog.get}/${catalogCode}/costs`);
        return data;
    }

    // Helper function to fetch landing zone details
    const fetchLandingZoneDetails = async (orderData) => {
        const isProduction = orderData?.environment === 'Production';
        if (
            orderData?.appProfile &&
            orderData?.sector &&
            orderData?.department &&
            orderData?.appRegion &&
            orderData?.environment &&
            (!isProduction || ['yes', 'no'].includes(orderData?.isHighAvailability))
        ) {
            const payload = {
                applicationServiceName: orderData.appProfile,
                sector: orderData.sector,
                environment: orderData.environment,
                department: orderData.department,
                applicationRegion: orderData.appRegion,
                ...(isProduction && { highAvailability: orderData?.isHighAvailability === 'yes' }),
            };
            try {
                const landingZoneResponse = await axios.post(endPoints.aws.Ec2.awsLandingZone, payload);
                return landingZoneResponse?.data;
            } catch (error) {
                return null;
            }
        }
        return null;
    };

    // Helper function to fetch drPriority from app profile
    const fetchDrPriority = async (orderData) => {
        if (orderData?.appProfile) {
            const payload = {
                businessApplication: null,
                applicationService: orderData.appProfile,
            };
            try {
                const response = await axios.post(endPoints.riServices.COMMON.businessCiDetails, payload);
                const drRecoveryPriority = response?.data?.data?.applications?.[0]?.drRecoveryPriority || null;
                return drRecoveryPriority;
            } catch (error) {
                return null;
            }
        }
        return null;
    };

    // Helper function to fetch disaster recovery details
    const fetchDisasterRecoveryDetails = async (orderData) => {
        if (orderData?.drPriority && orderData?.environment && orderData?.appTierCode) {
            const payload = {
                drPriority: orderData?.drPriority,
                environment: orderData?.environment,
                appTierCode: orderData?.appTierCode,
            };
            try {
                const drResponse = await axios.post(endPoints.ssp.vm.disasterRecoveryOptions, payload);
                return drResponse?.data?.metallic || null;
            } catch (error) {
                return null;
            }
        }
        return null;
    };
    async function checkCatalog() {
        const isWindows = 'Windows';
        const selector = isWindows ? 'azure_windows_vm_host' : 'azure_rhel_vm_host';
        const data = cmpCatalogs?.find((item) => item?.catalogDefinitionId === catalogCode);
        const costs = await getCosts();
        setVmDataDisk({
            ...vmDataDisk,
            diskSizeInGib: data?.provisioning?.requestPayloadDefinition?.extra_vars[selector]?.os_disk_size_gb,
        });
        if (cartItemId && operation && !catalogServiceId) {
            const { data: cartItem } = await axios.get(`${endPoints.cart.getCartItem}/${cartItemId}`);
            const orderObj = formOrderObjFromCart(cartItem, data?.images?.sku);

            // Fetch drPriority if not already in cart data
            let drPriority = orderObj?.drPriority || null;
            if (!drPriority) {
                drPriority = await fetchDrPriority(orderObj);
            }

            const orderObjWithDrPriority = { ...orderObj, drPriority };
            const landingZoneDetails = await fetchLandingZoneDetails(orderObjWithDrPriority);

            // Derive awsAccount from awsAccountId during edit
            const matchedAccount = landingZoneDetails?.awsAccounts?.find((account) => account?.accountId === orderObjWithDrPriority?.awsAccountId);

            // Fetch metallic if not already in cart data
            let metallic = orderObj?.metallic || null;
            if (!metallic && drPriority && orderObj.environment && orderObj.appTierCode) {
                metallic = await fetchDisasterRecoveryDetails(orderObjWithDrPriority);
            }
            setOrder((previous) => ({
                ...previous,
                ...orderObjWithDrPriority,
                sku: data?.images?.sku,
                emptyCost: costs,
                landingZoneDetails,
                metallic,
                ...(matchedAccount && { awsAccount: matchedAccount.accountName }),
            }));
        }
        if (cartItemId && operation && catalogServiceId) {
            const { data: orderDetails } = await axios.get(`${endPoints.order.orderDetails}/${cartItemId}`);
            const orderObj = formOrderObjFromOrderDetails(catalogServiceId, orderDetails, data?.images?.sku);

            // Fetch drPriority if not already in order data
            let drPriority = orderObj.drPriority || null;
            if (!drPriority) {
                drPriority = await fetchDrPriority(orderObj);
            }

            const orderObjWithDrPriority = { ...orderObj, drPriority };
            const landingZoneDetails = await fetchLandingZoneDetails(orderObjWithDrPriority);

            // Derive awsAccount from awsAccountId during edit/resubmit
            const matchedAccount = landingZoneDetails?.awsAccounts?.find((account) => account?.accountId === orderObjWithDrPriority?.awsAccountId);

            // Fetch metallic if not already in order data
            let metallic = orderObj.metallic || null;
            if (!metallic && drPriority && orderObj.environment && orderObj.appTierCode) {
                metallic = await fetchDisasterRecoveryDetails(orderObjWithDrPriority);
            }
            setOrder((previous) => ({
                ...previous,
                ...orderObjWithDrPriority,
                sku: data?.images?.sku,
                landingZoneDetails,
                metallic,
                ...(matchedAccount && { awsAccount: matchedAccount.accountName }),
            }));
        } else {
            setOrder((previous) => ({
                ...previous,
                sku: data?.images?.sku,
                catalogCode,
                catalogType: data?.displayName,
                cloudProvider: data?.cloudProvider,
                cloudService: data?.cloudService,
                validationStatus: 'clearValidation',
                isTouched: false,
                emptyCost: costs,
            }));
        }
        setLoading(false);
    }

    const reset = () => {
        setOrder({ ...order, ...resetOrder });
        resetOrderId('');
        resetIsMandatoryTagsFullfilled(false);
        resetIsConfigDiskFulfilled(false);
        resetToggleSspModels(resetToggleSspModal);
        resetQuickConfig(undefined);
    };

    React.useEffect(() => {
        if (cmpCatalogs?.length && catalogCode) {
            // Check if user has access to this catalog, redirect if not
            const shouldRedirect = checkAndRedirectCatalog(catalogCode, history);
            if (shouldRedirect) {
                return; // Exit early if redirected
            }
            setLoading(true);
            reset();
            setVmDataDisk(undefined);
            checkCatalog();
        }
    }, [cmpCatalogs, catalogCode]);

    /* eslint-disable arrow-body-style */
    React.useEffect(() => {
        return () => reset();
        // eslint-disable-next-line
    }, []);

    return (
        <>
            <Helmet>
                <title>
                    {catalogCode && [...AwsSQLIaasCatalogDefinitionId]?.includes(catalogCode)
                        ? 'AWS SQL IAAS Database Provisioning'
                        : 'AWS OEL Oracle 19C Provisioning'}
                </title>
            </Helmet>
            <Suspense
                fallback={
                    <Box sx={ProvisioningStyles.provisioning_wrapper}>
                        <CircularProgress color="inherit" size={16} sx={ProvisioningStyles.circularProgressColor} />
                        <Typography variant="body2" sx={ProvisioningStyles.circularProgressColor}>
                            Loading
                        </Typography>
                    </Box>
                }
            >
                {isLoading && (
                    <Box sx={ProvisioningStyles.provisioning_wrapper}>
                        <CircularProgress color="inherit" size={16} sx={ProvisioningStyles.circularProgressColor} />
                        <Typography variant="body2" sx={ProvisioningStyles.circularProgressColor}>
                            Loading
                        </Typography>
                    </Box>
                )}
                {!isLoading && (
                    <Box sx={ProvisioningStyles.provisioningBox}>
                        <ProvisioningHeader />
                        <Box sx={ProvisioningStyles.sspform_wrapper} className="contentContainer">
                            <SspForm />
                        </Box>
                        <br />
                        <ApplicationDetails />
                        <AddedToCartConfirmModal />
                        <UpdatedToCartConfirmModal />
                        <ConfirmDD />
                        <ShowCostDetails />
                        <CurrentServerNameDetails />
                    </Box>
                )}
            </Suspense>
        </>
    );
};

export default React.memo(AWSOracle19CProvisioningPage);
